import { Plus } from 'lucide-react'
import { useCallback, useState } from 'react'

import Button from '@/elements/Button'
import Text from '@/elements/Text'
import Flex from '@/elements/Flex'
import FormInput from '@/elements/FormInput'
import FormSelect from '@/elements/FormSelect'
import Table from '@/components/Table'
import Modal from '@/components/Modal'
import useBreakpoints from '@/hooks/useBreakpoints'
import toast from '@/utils/toast'
import type { ExclusionWordResponse } from '@/api/types/Organization'
import { useExclusionWordsTable } from '@/hooks/tables/exclusionWordsTable'
import { useGetOrganizationExclusionWords } from '@/api/organization'

function ExclusionWords({
  individualKeywords,
  refetchIndividualKeywords,
  handleCreate,
  handleDelete
}: {
  individualKeywords?: ExclusionWordResponse[]
  refetchIndividualKeywords?: () => void
  handleCreate: (data: { word: string }) => Promise<void>
  handleDelete: (id: string) => Promise<void>
}) {
  const { isMobile } = useBreakpoints()
  const [wordToAdd, setWordToAdd] = useState<ExclusionWordResponse['word']>('')
  const [wordToDeleteId, setWordToDeleteId] = useState<ExclusionWordResponse['id']>()

  const { data: exclusionWordsData, refetch: refetchExclusionWords } = useGetOrganizationExclusionWords()
  const organizationExclusionWords = exclusionWordsData?.data?.exclusionWords || []

  const keyWords = individualKeywords || organizationExclusionWords
  const refetch = refetchIndividualKeywords || refetchExclusionWords

  const exclusionWordsTableData = useExclusionWordsTable(keyWords, id => setWordToDeleteId(id))

  const handleWord = useCallback(async (action: 'remove' | 'add') => {
    if (action === 'add' && wordToAdd.trim() !== '') {

      const isDuplicate = keyWords.some(keyWord =>
        keyWord.word.trim().toLowerCase() === wordToAdd.trim().toLowerCase()
      )
      if (isDuplicate) {
        toast.error('Exclusion word already exists')
        return
      }
      try {
        await handleCreate({ word: wordToAdd })
        toast.success('Exclusion word added successfully')
        refetch()
        setWordToAdd('')
      } catch {
        toast.error('Failed to add exclusion word')
      }
    } else if (action === 'remove' && wordToDeleteId) {
      try {
        await handleDelete(wordToDeleteId)
        toast.success('Exclusion word removed successfully')
        refetch()
        setWordToDeleteId(undefined)
      } catch {
        toast.error('Failed to remove exclusion word')
      }
    }
  }, [wordToAdd, wordToDeleteId, keyWords, handleCreate, refetch, handleDelete])

  return (
    <Flex wfull align='flex-end' gap={6}>
      <Flex
        wfull
        inline
        wrap
        gap={3}
        justify='flex-end'
        align={individualKeywords ? 'center' : 'flex-end'}
      >
        {individualKeywords ? (
          <FormSelect<string>
            name='new-exclusion-word'
            label='Contexts to Exclude '
            placeholder='Select word or phrase to exclude'
            options={organizationExclusionWords.map(ew => ew.word)}
            values={wordToAdd ? [wordToAdd] : []}
            optionValue={item => item}
            optionLabel={item => item}
            onChange={([val]) => setWordToAdd(val || '')}
            style={{ flex: 1 }}
            filterBy={(option, search) =>
              option.toLowerCase().includes(search.toLowerCase())
            }
            createCustomValue={search => search}
            instructions="You can select from your organization's list or add your own keywords or phrases."
          />
        ) : (
          <FormInput
            name='new-exclusion-word'
            label='Contexts to Exclude'
            placeholder='Enter word or phrase to exclude'
            value={wordToAdd}
            onChangeText={setWordToAdd}
            style={{ flex: 1 }}
          />
        )}
        <Flex
          mb={(individualKeywords && !isMobile) ? 3 : 'unset'}
        >
          <Button
            variant='primary'
            fullWidth={isMobile}
            onClick={() => handleWord('add')}
          >
            <Plus size={18} />
            Add
          </Button>
        </Flex>
      </Flex>
      <Table {...exclusionWordsTableData} />
      <Modal
        isOpen={!!wordToDeleteId}
        title='Delete Exclusion Word'
        onSave={() => handleWord('remove')}
        onCancel={() => setWordToDeleteId(undefined)}
        cancelLabel='No'
        saveLabel='Yes'
      >
        <Text color='black' style={{ hyphens: 'none' }}>
          Are you sure you want to delete this exclusion word? This action cannot be undone.
        </Text>
      </Modal>
    </Flex>
  )
}

export default ExclusionWords
